import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Bell, AlertTriangle, Clock, Send, Loader2 } from "lucide-react";

export default function Reminders() {
  const [items, setItems] = useState(null);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/reminders").then((r) => setItems(r.data));
  }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reminders/send-email");
      if (data?.result?.skipped) toast.error("Reminder email address not configured.");
      else toast.success("Reminder email sent.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send email.");
    } finally {
      setSending(false);
    }
  };

  if (!items) return <div className="text-slate-400 font-mono text-sm">Loading reminders…</div>;

  const overdue = items.filter((i) => i.overdue);
  const dueSoon = items.filter((i) => !i.overdue);

  const Row = ({ i, tone }) => (
    <div
      data-testid={`reminder-${i.id}`}
      onClick={() => navigate(`/orders/${i.id}`)}
      className="flex items-center justify-between border border-[#E6E4DD] rounded-sm p-4 bg-white hover:border-[#1C2331] transition-colors cursor-pointer"
    >
      <div>
        <div className="text-sm font-medium text-[#0A0D14]">
          {i.buyer_name || "Unknown buyer"} <span className="text-[#8CA3A7]">←</span> {i.seller_name || "Unknown seller"}
        </div>
        <div className="text-xs text-[#8CA3A7] font-mono mt-1">{i.po_number || "No PO#"} · {i.fabric_type || "—"} · {i.payment_terms || "no terms"}</div>
        {i.po_number && (
          <button
            data-testid={`reminder-status-link-${i.id}`}
            onClick={(e) => { e.stopPropagation(); navigate(`/status?po=${encodeURIComponent(i.po_number)}`); }}
            className="text-[11px] text-[#D95D39] font-medium mt-1 hover:underline"
          >
            View status →
          </button>
        )}
      </div>
      <div className="text-right">
        <div className="font-mono tabular-nums text-sm text-[#D95D39]">{fmtINR(i.outstanding_amount)}</div>
        <div className={`text-xs font-mono mt-1 ${tone === "red" ? "text-[#991B1B]" : "text-[#92400E]"}`}>
          {i.overdue
            ? `Overdue by ${Math.abs(i.days_until_due ?? 0)}d · due ${i.due_date || "—"}`
            : `Due in ${i.days_until_due}d · ${i.due_date || "—"}`}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="reminders-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7]">Payments</p>
          <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1">Reminders</h1>
          <p className="text-sm text-[#8CA3A7] mt-2">Orders that are overdue or coming due within 7 days.</p>
        </div>
        <button
          data-testid="send-reminder-email-btn"
          onClick={sendNow}
          disabled={sending}
          className="bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium flex items-center gap-2 shrink-0 transition-transform active:scale-95 hover:bg-[#2A3447] disabled:opacity-60"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? "Sending…" : "Send reminder email now"}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-16 text-center">
          <Bell className="w-8 h-8 text-[#8CA3A7] mx-auto mb-3" />
          <p className="text-sm text-[#4B5563]">All clear — nothing due soon or overdue.</p>
        </div>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="font-heading text-lg font-semibold text-[#991B1B] flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4" /> Overdue <span className="font-mono text-sm text-[#8CA3A7]">({overdue.length})</span>
            </h2>
            {overdue.length === 0 ? (
              <p className="text-sm text-slate-400 font-mono">None overdue.</p>
            ) : (
              <div className="space-y-3">{overdue.map((i) => <Row key={i.id} i={i} tone="red" />)}</div>
            )}
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-[#92400E] flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4" /> Due soon <span className="font-mono text-sm text-[#8CA3A7]">({dueSoon.length})</span>
            </h2>
            {dueSoon.length === 0 ? (
              <p className="text-sm text-slate-400 font-mono">Nothing due in the next 7 days.</p>
            ) : (
              <div className="space-y-3">{dueSoon.map((i) => <Row key={i.id} i={i} tone="amber" />)}</div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
